// AuthModal - Authentication modal with OAuth providers
// Security: No credentials stored in frontend, uses httpOnly cookies

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './AuthModal.css';

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
    redirectPath: string;
}

export default function AuthModal({ isOpen, onClose, redirectPath }: AuthModalProps) {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    if (!isOpen) return null;

    const handleGoogleAuth = () => {
        setIsLoading(true);
        // Redirect to backend OAuth endpoint
        window.location.href = `/api/auth/google?redirect=${encodeURIComponent(redirectPath)}`;
    };

    return (
        <div className="auth-modal-overlay" onClick={onClose}>
            <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
                <button className="auth-modal-close" onClick={onClose} aria-label={t('common.close')}>
                    <CloseIcon />
                </button>

                <div className="auth-modal-header">
                    <div className="auth-modal-logo">
                        <img src="/raisa.png" alt="Raisa AI" style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
                    </div>
                    <h2>{t('auth.continue_raisa')}</h2>
                    <p>{t('auth.save_trips')}</p>
                </div>

                <div className="auth-modal-body">
                    {/* OAuth Providers */}
                    <button
                        className="auth-btn auth-btn-google"
                        onClick={handleGoogleAuth}
                        disabled={isLoading}
                    >
                        <GoogleIcon />
                        <span>{isLoading ? t('common.redirecting') : t('auth.continue_google')}</span>
                    </button>
                </div>

                <div className="auth-modal-footer">
                    <p>
                        {t('auth.agree_terms')}{' '}
                        <a href={t('auth.terms_link')} target="_blank">{t('auth.terms')}</a> {t('auth.and')}{' '}
                        <a href={t('auth.privacy_link')} target="_blank">{t('auth.privacy')}</a>
                    </p>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// ICONS (inline SVG for bundle efficiency)
// =============================================================================

function CloseIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}


function GoogleIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
    );
}
